import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useStore } from '../store';
import { useToast } from './Toast';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function TradovateReconnectModal({ open, onClose }: Props) {
  const { user, lang, reloadFromCloud } = useStore();
  const { show } = useToast();
  const [connecting, setConnecting] = useState(false);
  const [exchanging, setExchanging] = useState(false);
  const nonceRef = useRef<string>('');

  const isHe = lang === 'he';

  const exchangeCode = useCallback(
    async (code: string, nonce: string) => {
      const storedNonce = sessionStorage.getItem('tv_oauth_nonce');
      if (!storedNonce || storedNonce !== nonce) {
        show(isHe ? 'שגיאת אבטחה — נסה שוב' : 'Security mismatch — try again', 'error');
        return;
      }
      sessionStorage.removeItem('tv_oauth_nonce');
      if (!supabase || !user?.id) return;
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const jwt = session?.access_token;
      if (!jwt) return;

      setExchanging(true);
      try {
        const res = await fetch('/api/tradovate-auth', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
          body: JSON.stringify({ action: 'exchange_code', user_id: user.id, code }),
        });

        const data = await res.json();
        if (!res.ok || !data.accounts) {
          show(
            isHe ? `שגיאה: ${data.error ?? res.status}` : `Error: ${data.error ?? res.status}`,
            'error',
          );
          return;
        }

        let tokenExpiresAt: string | null = null;
        try {
          tokenExpiresAt =
            (JSON.parse(data.token_json) as { expires_at?: string }).expires_at ?? null;
        } catch {}

        // Scope the token update to ONLY the Tradovate accounts returned by this OAuth
        // session. A user with multiple prop firms has a separate Tradovate login (and
        // separate token) per firm — updating every tradovate connection here would
        // clobber the other firms' tokens and cross-contaminate tradovate_account_id.
        const sessionAccountIds = (data.accounts as { id: number }[]).map((a) => a.id);
        if (sessionAccountIds.length === 0) {
          show(isHe ? 'לא נמצאו חשבונות Tradovate' : 'No Tradovate accounts found', 'error');
          return;
        }

        const { error: updateErr } = await supabase
          .from('broker_connections')
          .update({
            api_key: data.token_json,
            is_active: true,
            consecutive_failures: 0,
            token_error: null,
            ...(tokenExpiresAt ? { token_expires_at: tokenExpiresAt } : {}),
          })
          .eq('user_id', user.id)
          .eq('broker', 'tradovate')
          .in('tradovate_account_id', sessionAccountIds);

        if (updateErr) {
          show(isHe ? `שגיאה: ${updateErr.message}` : `DB error: ${updateErr.message}`, 'error');
          return;
        }

        localStorage.removeItem('tv_reconnect_dismissed');
        show(
          isHe ? '✓ Tradovate חובר מחדש בהצלחה' : '✓ Tradovate reconnected successfully',
          'success',
        );
        onClose();
        setTimeout(() => reloadFromCloud(), 300);
        fetch('/api/tradovate-sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
          body: JSON.stringify({ user_id: user.id }),
        })
          .then(() => reloadFromCloud())
          .catch(() => {});
      } catch (err) {
        show(isHe ? 'שגיאה בהתחברות Tradovate' : 'Could not reconnect Tradovate', 'error');
      } finally {
        setExchanging(false);
      }
    },
    [user?.id, isHe, show, onClose, reloadFromCloud],
  );

  // postMessage from OAuth popup
  useEffect(() => {
    if (!open) return;
    const handler = (e: MessageEvent) => {
      const allowed = ['https://traderyo.com', 'https://www.traderyo.com'];
      if (!allowed.includes(e.origin)) return;
      const { tv_code, tv_nonce, tv_error } = (e.data ?? {}) as {
        tv_code?: string;
        tv_nonce?: string;
        tv_error?: string;
      };
      if (tv_error) {
        show(isHe ? `Tradovate: ${tv_error}` : `Tradovate error: ${tv_error}`, 'error');
        return;
      }
      if (tv_code && tv_nonce) {
        localStorage.removeItem('tv_oauth_result');
        localStorage.removeItem('tv_oauth_error');
        localStorage.removeItem('tv_oauth_ts');
        exchangeCode(tv_code, tv_nonce);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [open, exchangeCode, isHe, show]);

  // Mobile redirect: check URL params on open
  const redirectHandled = useRef(false);
  useEffect(() => {
    if (!open || redirectHandled.current || !user?.id) return;
    const params = new URLSearchParams(window.location.search);
    const tv_code = params.get('tv_code');
    const tv_nonce = params.get('tv_nonce');
    if (tv_code && tv_nonce) {
      redirectHandled.current = true;
      localStorage.removeItem('tv_oauth_result');
      localStorage.removeItem('tv_oauth_error');
      localStorage.removeItem('tv_oauth_ts');
      const clean = new URL(window.location.href);
      clean.searchParams.delete('tv_code');
      clean.searchParams.delete('tv_nonce');
      window.history.replaceState({}, '', clean.toString());
      exchangeCode(tv_code, tv_nonce);
    }
  }, [open, user?.id, exchangeCode]);

  const connect = async () => {
    if (!supabase || !user?.id) return;
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const jwt = session?.access_token;
    if (!jwt) {
      show(isHe ? 'לא מחובר' : 'Not authenticated', 'error');
      return;
    }

    setConnecting(true);
    try {
      const nonce = crypto.randomUUID();
      nonceRef.current = nonce;
      sessionStorage.setItem('tv_oauth_nonce', nonce);

      const res = await fetch('/api/tradovate-oauth-start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ user_id: user.id, nonce }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        show(
          isHe ? `שגיאה: ${data.error ?? res.status}` : `Error: ${data.error ?? res.status}`,
          'error',
        );
        return;
      }

      const isMobile =
        /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth <= 768;
      if (isMobile) {
        window.location.href = data.url;
        return;
      }

      const w = 600,
        h = 700;
      const left = Math.round((window.screen.width - w) / 2);
      const top = Math.round((window.screen.height - h) / 2);
      const popup = window.open(
        data.url,
        'tradovate-oauth',
        `width=${w},height=${h},left=${left},top=${top},resizable=yes`,
      );
      if (!popup) {
        window.location.href = data.url;
        return;
      }

      const poll = window.setInterval(() => {
        try {
          const ts = localStorage.getItem('tv_oauth_ts');
          if (!ts || Date.now() - parseInt(ts) > 120_000) {
            if (popup.closed) clearInterval(poll);
            return;
          }
          const result = localStorage.getItem('tv_oauth_result');
          const errVal = localStorage.getItem('tv_oauth_error');
          if (result) {
            localStorage.removeItem('tv_oauth_result');
            localStorage.removeItem('tv_oauth_ts');
            clearInterval(poll);
            const { code, nonce: n } = JSON.parse(result) as { code: string; nonce: string };
            exchangeCode(code, n);
          } else if (errVal) {
            localStorage.removeItem('tv_oauth_error');
            localStorage.removeItem('tv_oauth_ts');
            clearInterval(poll);
            show(isHe ? `Tradovate: ${errVal}` : `Tradovate error: ${errVal}`, 'error');
          } else if (popup.closed) {
            clearInterval(poll);
          }
        } catch {
          clearInterval(poll);
        }
      }, 500);
    } catch {
      show(isHe ? 'שגיאה בחיבור Tradovate' : 'Could not start Tradovate OAuth', 'error');
    } finally {
      setConnecting(false);
    }
  };

  if (!open) return null;

  const busy = connecting || exchanging;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        style={{
          background: 'var(--s1)',
          border: '1px solid var(--bd2)',
          borderRadius: 16,
          padding: '36px 32px',
          maxWidth: 440,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'rgba(255,170,68,0.12)',
            border: '1px solid rgba(255,170,68,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: 24,
          }}
        >
          🔌
        </div>

        <h2
          style={{ color: 'var(--t1)', margin: '0 0 10px', fontSize: '1.15rem', fontWeight: 700 }}
        >
          {isHe ? 'חיבור Tradovate פג' : 'Tradovate Session Expired'}
        </h2>
        <p style={{ color: 'var(--t2)', margin: '0 0 28px', fontSize: '.9rem', lineHeight: 1.55 }}>
          {isHe
            ? 'הסשן שלך עם Tradovate פג תוקף. לחץ להתחבר מחדש — תופיע חלון Tradovate ישירות.'
            : 'Your Tradovate session has expired. Click below to reconnect — the Tradovate login window will open directly.'}
        </p>

        <button
          onClick={connect}
          disabled={busy}
          style={{
            width: '100%',
            padding: '14px 0',
            borderRadius: 10,
            border: 'none',
            background: busy ? 'var(--s3)' : 'var(--g)',
            color: busy ? 'var(--t3)' : '#fff',
            fontWeight: 700,
            fontSize: '1rem',
            cursor: busy ? 'not-allowed' : 'pointer',
            transition: 'background .15s',
            marginBottom: 12,
          }}
        >
          {busy
            ? isHe
              ? 'מתחבר...'
              : 'Connecting...'
            : isHe
              ? '🔗 התחבר מחדש ל-Tradovate'
              : '🔗 Reconnect Tradovate'}
        </button>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 24 }}>
          <button
            onClick={onClose}
            disabled={busy}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--t3)',
              fontSize: '.85rem',
              cursor: busy ? 'not-allowed' : 'pointer',
              padding: '6px 12px',
            }}
          >
            {isHe ? 'אחר כך' : 'Remind me later'}
          </button>
          <button
            onClick={() => {
              localStorage.setItem('tv_reconnect_dismissed', '1');
              onClose();
            }}
            disabled={busy}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--t3)',
              fontSize: '.85rem',
              cursor: busy ? 'not-allowed' : 'pointer',
              padding: '6px 12px',
              textDecoration: 'underline',
            }}
          >
            {isHe ? 'אל תציג שוב' : "Don't show again"}
          </button>
        </div>
      </div>
    </div>
  );
}
